import {
  AppIcon,
  College,
  DataTable,
  formatCurrency,
  addHttpIfMissing,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LineIconItem } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/LineIconItem';

import { Box, SxProps, Theme, Typography, Button } from '@mui/material';
import ReactMarkdown from 'react-markdown';

const THE_COST_STRINGS = appStrings.modules.college.theCost;

type TheCostProps = Readonly<{
  college: College;
}>;

export const TheCost = ({ college }: TheCostProps) => {
  const HeaderButton = college.net_price_calculator_url && (
    <Button
      hierarchy="Secondary gray"
      size="md"
      startIcon={
        <AppIcon iconName="Calculator" diameter="20px" color="Colors/Text/text-secondary" />
      }
      onClick={() => {
        if (college.net_price_calculator_url) {
          window.open(addHttpIfMissing(college.net_price_calculator_url), '_blank');
        }
      }}
    >
      {THE_COST_STRINGS.header.netPriceButton}
    </Button>
  );

  return (
    <Box sx={containerSx}>
      {/* TITLE AND NET PRICE CALCULATOR BUTTON */}
      <Box sx={titleContainerSx}>
        <Box sx={titleSx}>
          <Typography component="div" variant="display-sm-medium">
            {THE_COST_STRINGS.header.title}
          </Typography>

          <Box sx={largeScreenButtonSx}>{HeaderButton}</Box>
        </Box>
        <Box sx={summaryContainerSx}>
          <Typography component="div" sx={summarySx} variant="text-sm-regular">
            <ReactMarkdown
              components={{
                p: ({ ...props }) => <p style={{ margin: 0 }} {...props} />,
              }}
            >
              {college.section_the_cost_summary}
            </ReactMarkdown>
          </Typography>

          <Box sx={smallScreenButtonSx}>{HeaderButton}</Box>
        </Box>
      </Box>

      <LineIconItem
        title={THE_COST_STRINGS.header.costByFamilyIncome}
        content={{
          type: 'TOOLTIP',
          toolTipText: THE_COST_STRINGS.header.costByFamilyIncomeTooltip,
        }}
      />

      {/* TABLE OF COST BY FAMILY INCOME AND COST FIGURES */}
      <Box sx={tableFiguresContainerSx}>
        <Box sx={dataTableContainerSx}>
          <DataTable
            data={{
              headers: [
                THE_COST_STRINGS.dataTable.familyIncome,
                THE_COST_STRINGS.dataTable.averageAnnualCost,
              ],
              rows: [
                {
                  [THE_COST_STRINGS.dataTable.familyIncome]:
                    THE_COST_STRINGS.dataTable.net_price_income_level_0_30000,
                  [THE_COST_STRINGS.dataTable.averageAnnualCost]:
                    college.net_price_income_level_0_30000
                      ? formatCurrency(college.net_price_income_level_0_30000)
                      : THE_COST_STRINGS.notAvailable,
                },
                {
                  [THE_COST_STRINGS.dataTable.familyIncome]:
                    THE_COST_STRINGS.dataTable.net_price_income_level_30001_48000,
                  [THE_COST_STRINGS.dataTable.averageAnnualCost]:
                    college.net_price_income_level_30001_48000
                      ? formatCurrency(college.net_price_income_level_30001_48000)
                      : THE_COST_STRINGS.notAvailable,
                },
                {
                  [THE_COST_STRINGS.dataTable.familyIncome]:
                    THE_COST_STRINGS.dataTable.net_price_income_level_48001_75000,
                  [THE_COST_STRINGS.dataTable.averageAnnualCost]:
                    college.net_price_income_level_48001_75000
                      ? formatCurrency(college.net_price_income_level_48001_75000)
                      : THE_COST_STRINGS.notAvailable,
                },
                {
                  [THE_COST_STRINGS.dataTable.familyIncome]:
                    THE_COST_STRINGS.dataTable.net_price_income_level_75001_110000,
                  [THE_COST_STRINGS.dataTable.averageAnnualCost]:
                    college.net_price_income_level_75001_110000
                      ? formatCurrency(college.net_price_income_level_75001_110000)
                      : THE_COST_STRINGS.notAvailable,
                },
                {
                  [THE_COST_STRINGS.dataTable.familyIncome]:
                    THE_COST_STRINGS.dataTable.net_price_income_level_110001_plus,
                  [THE_COST_STRINGS.dataTable.averageAnnualCost]:
                    college.net_price_income_level_110001_plus
                      ? formatCurrency(college.net_price_income_level_110001_plus)
                      : THE_COST_STRINGS.notAvailable,
                },
              ],
            }}
          />
        </Box>

        <Box sx={costFiguresContainerSx}>
          {/* We have to wrap the line items in a container to align the items properly */}
          <Box sx={lineItemsContainerSx}>
            {college.tuition_in_state && (
              <LineIconItem
                title={THE_COST_STRINGS.costFigureTitles.inStateTuition}
                iconName="MarkerPin05"
                text={formatCurrency(college.tuition_in_state)}
                content={{
                  type: 'TOOLTIP',
                  toolTipText: THE_COST_STRINGS.costFigureTitles.inStateTuitionTooltip,
                }}
              />
            )}

            {college.tuition_out_of_state && (
              <LineIconItem
                title={THE_COST_STRINGS.costFigureTitles.outOfStateTuition}
                iconName="Plane"
                text={formatCurrency(college.tuition_out_of_state)}
                content={{
                  type: 'TOOLTIP',
                  toolTipText: THE_COST_STRINGS.costFigureTitles.outOfStateTuitionTooltip,
                }}
              />
            )}

            {college.room_and_board && (
              <LineIconItem
                title={THE_COST_STRINGS.costFigureTitles.roomAndBoard}
                iconName="HomeSmile"
                text={formatCurrency(college.room_and_board)}
                content={{
                  type: 'TOOLTIP',
                  toolTipText: THE_COST_STRINGS.costFigureTitles.roomAndBoardTooltip,
                }}
              />
            )}

            {college.average_financial_aid && (
              <LineIconItem
                title={THE_COST_STRINGS.costFigureTitles.averageFinancialAid}
                iconName="BankNote01"
                text={formatCurrency(college.average_financial_aid)}
                content={{
                  type: 'TOOLTIP',
                  toolTipText: THE_COST_STRINGS.costFigureTitles.averageFinancialAidTooltip,
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  borderTop: `1px solid ${backrs.colors['Colors/Foreground/fg-quaternary']}`,
  mb: backrs.spacing['spacing-6xl'],
  marginX: backrs.spacing['spacing-4xl'],
  '@media (max-height: 768px)': {
    mx: backrs.spacing['spacing-4xl'],
  },
  '@media (max-width: 414px)': {
    mx: backrs.spacing['spacing-2xl'],
  },
});

const titleContainerSx: SxProps<Theme> = ({ backrs }) => ({
  marginY: backrs.spacing['spacing-2xl'],
  display: 'flex',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
});

const titleSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  flexWrap: 'wrap',
};

const largeScreenButtonSx: SxProps<Theme> = ({ backrs }) => ({
  display: { xs: 'none', sm: 'block' },
  alignSelf: 'flex-end',
  marginBottom: backrs.spacing['spacing-xs'],
});

const smallScreenButtonSx: SxProps<Theme> = ({ backrs }) => ({
  display: { xs: 'block', sm: 'none' },
  marginTop: backrs.spacing['spacing-xl'],
});

const tableFiguresContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  mt: backrs.spacing['spacing-2xl'],

  flexDirection: {
    xs: 'column',
    md: 'row',
  },
  gap: backrs.spacing['spacing-3xl'],
  flexWrap: { xs: 'wrap', lg: 'nowrap' },
});

const costFiguresContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: {
    xs: 'flex-start',
    lg: 'center',
  },
  width: '100%',
};

const lineItemsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
});

const dataTableContainerSx: SxProps<Theme> = {
  width: 'fit-content',
};

const summarySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const summaryContainerSx: SxProps<Theme> = {
  width: { xs: '100%', sm: '66%', md: '100%', lg: '66%' },
};
